import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import InputLabel from "@material-ui/core/InputLabel";
import { withStyles } from "@material-ui/core/styles";
import { connect } from "react-redux";
import { compose } from "redux";
import { fetchTags } from "../../actions/tags";
import { getReferenceDataType, getTags } from "../../reducers";
import usersApi from "../../services/api/users";
import { roles } from "@certane/arcadia-web-components";
import Avatar from "../common/Avatar.tsx";
import ChoiceChips from "../common/ChoiceChips";
import ClearButton from "../common/ClearButton";
import DebouncedTextField from "../common/DebouncedTextField";
import FiltersBar from "../common/FiltersBar";
import RefMultiSelect from "../common/RefMultiSelect";
import { tagsToOptions } from "../forms/menuItems";
import ListPicker from "../ListPicker";
import { useAuth0 } from "@auth0/auth0-react";

const styles = (theme) => ({
  clearButtonSearchField: {
    padding: theme.spacing(0.5),
    position: "absolute",
    right: 0,
    top: `-${theme.spacing(2)}px`,
  },
  searchField: {
    fontSize: "0.8125rem",
    lineHeight: "1.1875em",
    minWidth: "400px",
  },
  filterWidget: {
    marginTop: theme.spacing(2),
  },
});

const RiskFilters = ({
  classes,
  filter,
  updateFilter,
  riskRatings,
  riskAppetites,
  riskCategories,
  tags,
  localFetchTags,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [ownerPickerOpen, setOwnerPickerOpen] = useState(false);
  const [selectedOwnedBy, setSelectedOwnedBy] = useState([]);

  useEffect(() => {
    (async () => {
      const accessToken = await getAccessTokenSilently();
      localFetchTags(accessToken);

      // initially load the users currently selected in the filter
      if (filter.ownerIds && filter.ownerIds.length > 0) {
        usersApi
          .search(
            {
              limit: filter.ownerIds.length,
              offset: 0,
              roles: [roles.COMPLIANCE_OWNER, roles.COMPLIANCE_OFFICER],
              userIds: filter.ownerIds,
            },
            null,
            accessToken
          )
          .then((response) => setSelectedOwnedBy(response.results));
      }
    })();
  }, [getAccessTokenSilently]);

  const setOwnedBy = (users) => {
    setSelectedOwnedBy(users);
    updateFilter(
      "ownerIds",
      users.map((user) => user.id)
    );
    setOwnerPickerOpen(false);
  };

  const asyncFetch = async (pickerFilter, pagination, abortController) => {
    const accessToken = await getAccessTokenSilently();
    const searchParameters = {
      ...pickerFilter,
      limit: pagination.pageSize,
      offset: pagination.offset,
      roles: [roles.COMPLIANCE_OWNER, roles.COMPLIANCE_OFFICER],
      orderByField: "name",
    };
    return usersApi.search(searchParameters, abortController, accessToken);
  };

  const riskRegisterOptions = tagsToOptions(
    tags.filter((tag) => tag.type === "RISK_REGISTER")
  );

  return (
    <>
      <ListPicker
        data-cy="select"
        title="Select owners"
        actionText="Select"
        open={ownerPickerOpen}
        onClose={() => setOwnerPickerOpen(false)}
        onSubmit={(u) => setOwnedBy(u)}
        datasource={asyncFetch}
        selected={selectedOwnedBy}
        isMulti
        clearable
        toOption={(user) => ({
          label: user.name || "Unknown",
          id: user.id,
          email: user.email,
        })}
        fromOption={(option) => ({
          name: option.label,
          id: option.id,
          email: option.email,
        })}
        renderIcon={(user, size) => (
          <Avatar
            data-cy={user.name}
            email={user.email}
            name={user.name}
            size={size}
            round
          />
        )}
      />
      <FiltersBar>
        <>
          <InputLabel data-cy="search">Search</InputLabel>
          <div className={classes.filterWidget}>
            {filter.textSearch && (
              <ClearButton
                className={classes.clearButtonSearchField}
                onClear={() => updateFilter("textSearch", "")}
              />
            )}
            <DebouncedTextField
              data-cy={filter.textSearch}
              value={filter.textSearch}
              onChange={(text) => updateFilter("textSearch", text)}
              placeholder="Search across risk titles"
              margin="none"
              InputProps={{
                className: classes.searchField,
              }}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="ownership">Ownership</InputLabel>
          <div className={classes.filterWidget}>
            <ChoiceChips
              value={filter.ownerIds.length > 0 ? "ownerIds" : null}
              options={[
                {
                  label: "Owned by",
                  value: "ownerIds",
                  badgeValue: filter.ownerIds.length,
                },
              ]}
              onChange={() => setOwnerPickerOpen(true)}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="riskRegister">Risk register</InputLabel>
          <div className={classes.filterWidget}>
            <RefMultiSelect
              title="Select risk registers"
              data-cy={filter.riskRegisters}
              value={filter.riskRegisters}
              options={riskRegisterOptions}
              onChange={(value) => updateFilter("riskRegisters", value)}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="riskRating">Risk rating</InputLabel>
          <div className={classes.filterWidget}>
            <RefMultiSelect
              title="Select risk ratings"
              data-cy={filter.riskRatings}
              value={filter.riskRatings}
              options={riskRatings}
              onChange={(value) => updateFilter("riskRatings", value)}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="riskCategory">Risk category</InputLabel>
          <div className={classes.filterWidget}>
            <RefMultiSelect
              title="Select risk categories"
              data-cy={filter.categories}
              value={filter.categories}
              options={riskCategories}
              onChange={(value) => updateFilter("categories", value)}
            />
          </div>
        </>
        <>
          <InputLabel data-cy="riskCategoryAppetite">
            Risk category appetite
          </InputLabel>
          <div className={classes.filterWidget}>
            <RefMultiSelect
              data-cy={filter.appetites}
              title="Select risk category appetites"
              value={filter.appetites}
              options={riskAppetites}
              onChange={(value) => updateFilter("appetites", value)}
            />
          </div>
        </>
      </FiltersBar>
    </>
  );
};

RiskFilters.propTypes = {
  classes: PropTypes.object.isRequired,
  filter: PropTypes.object.isRequired,
  updateFilter: PropTypes.func.isRequired,

  // redux
  riskRatings: PropTypes.object.isRequired,
  riskAppetites: PropTypes.object.isRequired,
  riskCategories: PropTypes.object.isRequired,
  tags: PropTypes.array.isRequired,
  localFetchTags: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  riskRatings: getReferenceDataType(state, "RiskRating"),
  riskCategories: getReferenceDataType(state, "RiskCategory"),
  riskAppetites: getReferenceDataType(state, "RiskAppetite"),
  tags: getTags(state),
});

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    localFetchTags: fetchTags,
  })
)(RiskFilters);
